import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';
import { productService } from '../../services';
import { API_BASE_URL } from '../../config/constants';

const RECENT_KEY = 'mwl.recentProductSearches';
const RECENT_LIMIT = 5;
const DEBOUNCE_MS = 250;

interface VariantHit {
  id: string;
  name: string;
  part_number: string;
  image_url: string | null;
  group_key: string;
  group_name: string | null;
  category_label: string | null;
}

/** Item rendered inside the dropdown. Either a recent term or a variant hit. */
type SearchOption =
  | { kind: 'recent'; label: string }
  | { kind: 'variant'; hit: VariantHit };

const isRecent = (o: SearchOption): o is { kind: 'recent'; label: string } => o.kind === 'recent';

const resolveImageUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) {
    return `${API_BASE_URL.replace('/api/v1', '')}${url}`;
  }
  return url;
};

const loadRecentSearches = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string').slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
};

const saveRecentSearch = (term: string): string[] => {
  const trimmed = term.trim();
  if (!trimmed) return loadRecentSearches();
  const existing = loadRecentSearches().filter((s) => s.toLowerCase() !== trimmed.toLowerCase());
  const next = [trimmed, ...existing].slice(0, RECENT_LIMIT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota errors */
  }
  return next;
};

interface Props {
  /** Current search term used by the page filter. */
  value: string;
  /** Apply a free-text search (used for filter chips, "Enter" key, etc.). */
  onSearchChange: (term: string) => void;
  /** Called when a specific variant suggestion is picked — opens the group dialog. */
  onVariantSelect: (groupKey: string, variantId: string) => void;
  /** Optional width override (defaults to 100%). */
  width?: number | string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

/**
 * Catalog search box with typeahead suggestions and recent-search history.
 *
 * - Debounced async fetch (250ms) against /products/catalog/suggestions.
 * - Empty input → shows last 5 recent searches.
 * - Variant click → opens that variant's group dialog directly.
 * - Free-text Enter → applies as filter and persists to recent list.
 */
const ProductSearchAutocomplete: React.FC<Props> = ({
  value,
  onSearchChange,
  onVariantSelect,
  width,
  size = 'small',
  fullWidth = false,
}) => {
  const { t, i18n } = useTranslation();
  const isId = i18n.language === 'id';

  const [inputValue, setInputValue] = useState(value);
  const [variants, setVariants] = useState<VariantHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState<string[]>(loadRecentSearches);

  // Keep input in sync if parent clears the search externally.
  useEffect(() => {
    if (value !== inputValue) setInputValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced fetch as the user types.
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef<string>('');
  useEffect(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);

    const trimmed = inputValue.trim();
    if (!trimmed) {
      setVariants([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchTimerRef.current = setTimeout(async () => {
      lastQueryRef.current = trimmed;
      try {
        const res = await productService.getCatalogSuggestions(trimmed, 8);
        // Race protection: only apply if still latest.
        if (lastQueryRef.current === trimmed) {
          setVariants(res.variants || []);
        }
      } finally {
        if (lastQueryRef.current === trimmed) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    };
  }, [inputValue]);

  // Build the dropdown options. Recents shown when input is empty, otherwise
  // show variant hits. Recents are only added when there is no typed text so
  // they don't fight with live results.
  const options: SearchOption[] = useMemo(() => {
    if (!inputValue.trim()) {
      return recents.map((label) => ({ kind: 'recent' as const, label }));
    }
    return variants.map((hit) => ({ kind: 'variant' as const, hit }));
  }, [inputValue, variants, recents]);

  const handleSelect = (
    _: any,
    option: SearchOption | string | null,
    reason?: string,
  ) => {
    // The built-in MUI clear button calls onChange with (null, 'clear').
    if (option == null) {
      if (reason === 'clear') {
        setInputValue('');
        onSearchChange('');
      }
      return;
    }
    if (typeof option === 'string') {
      // freeSolo string entry (Enter on raw text)
      const term = option.trim();
      if (!term) return;
      setRecents(saveRecentSearch(term));
      onSearchChange(term);
      return;
    }
    if (isRecent(option)) {
      setInputValue(option.label);
      onSearchChange(option.label);
      return;
    }
    // Variant hit → record the search term, fire dialog open, but ALSO apply
    // the variant name as the active search filter so the catalog narrows down
    // to the right group below the dialog.
    const term = option.hit.name;
    setRecents(saveRecentSearch(term));
    setInputValue(term);
    onVariantSelect(option.hit.group_key, option.hit.id);
  };

  return (
    <Autocomplete<SearchOption, false, false, true>
      freeSolo
      fullWidth={fullWidth}
      size={size}
      options={options}
      inputValue={inputValue}
      filterOptions={(opts) => opts /* server-side filtering */}
      onInputChange={(_, v) => setInputValue(v)}
      onChange={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          // Default Autocomplete Enter selects highlighted option; if none,
          // commit the typed text as a free-text search filter.
          const term = inputValue.trim();
          if (term && !options.some((o) => isRecent(o) && o.label === term)) {
            // Wait a tick so the Autocomplete's own selection logic runs
            // first; if it didn't fire, we apply the free text.
            setTimeout(() => {
              if (lastQueryRef.current !== '__handled__') {
                setRecents(saveRecentSearch(term));
                onSearchChange(term);
              }
            }, 0);
          }
        }
      }}
      isOptionEqualToValue={(opt, val) => {
        if (typeof val === 'string') return false;
        if (isRecent(opt) && isRecent(val)) return opt.label === val.label;
        if (!isRecent(opt) && !isRecent(val)) return opt.hit.id === val.hit.id;
        return false;
      }}
      getOptionLabel={(opt) => {
        if (typeof opt === 'string') return opt;
        return isRecent(opt) ? opt.label : opt.hit.name;
      }}
      loading={loading}
      noOptionsText={
        inputValue.trim()
          ? (isId ? 'Tidak ada hasil' : 'No matches')
          : (isId ? 'Mulai mengetik untuk mencari…' : 'Start typing to search…')
      }
      groupBy={(opt) => (isRecent(opt) ? (isId ? 'Pencarian Terakhir' : 'Recent searches') : (isId ? 'Saran' : 'Suggestions'))}
      renderGroup={(params) => (
        <li key={params.key}>
          <ListSubheader
            sx={{
              bgcolor: 'grey.50',
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontWeight: 600,
              color: 'text.secondary',
              lineHeight: '28px',
            }}
          >
            {params.group}
          </ListSubheader>
          <Box>{params.children}</Box>
        </li>
      )}
      renderOption={(props, opt) => {
        if (isRecent(opt)) {
          return (
            <Box component="li" {...props} key={`recent-${opt.label}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <HistoryIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2">{opt.label}</Typography>
            </Box>
          );
        }
        const hit = opt.hit;
        const img = resolveImageUrl(hit.image_url);
        return (
          <Box component="li" {...props} key={`v-${hit.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <Avatar
              src={img || undefined}
              alt={hit.name}
              variant="rounded"
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'grey.100',
                '& img': { objectFit: 'contain', p: 0.5 },
              }}
            >
              {!img && hit.name.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {hit.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {hit.category_label && <>{hit.category_label} · </>}
                {hit.part_number}
              </Typography>
            </Box>
          </Box>
        );
      }}
      sx={{ width: width ?? (fullWidth ? '100%' : 450) }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('products.searchPlaceholder')}
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProductSearchAutocomplete;
